import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MEZZI = [["bonifico", "Bonifico"], ["pos", "POS"], ["contanti", "Contanti"], ["assegno", "Assegno"], ["altro", "Altro"]];
const LBL = { bonifico: "Bonifico", sepa: "Bonifico", pos: "POS", carta: "POS", contanti: "Contanti", assegno: "Assegno", altro: "Altro", ricevuta_bancaria: "Ricevuta bancaria" };

export const BulkMezzoDialog = ({ open, onOpenChange, selected, onDone, onOpenInvoice }) => {
  const [mezzo, setMezzo] = useState("bonifico");
  const [conflicts, setConflicts] = useState(null);
  const [showList, setShowList] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setConflicts(null); setShowList(false); }
  }, [open]);

  const apply = async (overwrite) => {
    setBusy(true);
    try {
      const { data } = await api.post("/fatture/bulk-mezzo", { ids: selected.map((f) => f.id), mezzo, overwrite });
      toast.success(`Metodo assegnato a ${data.updated} fatture`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore assegnazione metodo"); }
    finally { setBusy(false); }
  };

  const confirm = () => {
    const withMethod = selected.filter((f) => f.mezzo_pagamento);
    if (withMethod.length > 0) setConflicts(withMethod);
    else apply(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-mezzo-dialog">
        <DialogHeader>
          <DialogTitle>Assegna metodo di pagamento</DialogTitle>
          <DialogDescription>{selected.length} fatture selezionate</DialogDescription>
        </DialogHeader>
        {!conflicts ? (
          <div className="space-y-4">
            <div>
              <Label>Metodo</Label>
              <Select value={mezzo} onValueChange={setMezzo}>
                <SelectTrigger data-testid="bulk-mezzo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEZZI.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`bulk-mezzo-${v}`}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="bulk-mezzo-cancel">Annulla</Button>
              <Button onClick={confirm} disabled={busy} data-testid="bulk-mezzo-confirm">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Assegna
              </Button>
            </div>
          </div>
        ) : (
          <ConflictView conflicts={conflicts} showList={showList} setShowList={setShowList}
                        busy={busy} apply={apply} onOpenInvoice={onOpenInvoice} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const ConflictView = ({ conflicts, showList, setShowList, busy, apply, onOpenInvoice }) => (
  <div className="space-y-4" data-testid="bulk-mezzo-conflicts">
    <div className="flex gap-2 items-start rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
      <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
      <div>
        <div className="font-semibold">Ci sono fatture che hanno già un metodo, cosa faccio?</div>
        <div className="text-xs text-muted-foreground">{conflicts.length} fatture hanno già un metodo assegnato.</div>
      </div>
    </div>
    <button type="button" className="text-xs underline text-primary" onClick={() => setShowList(!showList)} data-testid="bulk-mezzo-toggle-list">
      {showList ? "Nascondi elenco" : "Vedi elenco fatture"}
    </button>
    {showList && (
      <div className="max-h-56 overflow-y-auto border rounded-md divide-y" data-testid="bulk-mezzo-conflict-list">
        {conflicts.map((f) => (
          <button key={f.id} type="button" onClick={() => onOpenInvoice(f)} data-testid={`bulk-mezzo-conflict-${f.id}`}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-secondary flex justify-between gap-2">
            <span><b>{f.numero || "s.n."}</b> · {f.controparte} · {f.data}</span>
            <span className="text-muted-foreground">{LBL[f.mezzo_pagamento] || f.mezzo_pagamento}</span>
          </button>
        ))}
      </div>
    )}
    <div className="flex flex-wrap justify-end gap-2">
      <Button variant="outline" disabled={busy} onClick={() => apply(false)} data-testid="bulk-mezzo-only-empty">Solo quelle senza metodo</Button>
      <Button variant="destructive" disabled={busy} onClick={() => apply(true)} data-testid="bulk-mezzo-overwrite">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sovrascrivi
      </Button>
    </div>
  </div>
);
